// Content registry for the in-page "guide" popups (the small eye in each header).
//
// Each entry explains, in plain language, what a page is for and where to look.
// The dashboard entry additionally carries `audiences` — a by-role breakdown of
// the most useful functions for academics, producers/professionals and
// policymakers, so a brand-new user can see "what is here for someone like me".
//
// Kept as plain strings (English) so the whole guide is editable in one file,
// matching how the hub pages already hold their copy. Easy to localise later.

import {
  FaGraduationCap,
  FaWineBottle,
  FaLandmark,
} from "react-icons/fa";

// Audience groups used by the dashboard overview. Each `item` may carry an
// optional `to` route so "where to look" is actually clickable.
const DASHBOARD_AUDIENCES = [
  {
    key: "academics",
    label: "For academics & students",
    icon: FaGraduationCap,
    blurb:
      "Run research, share your work, and connect with peers worldwide.",
    items: [
      {
        title: "Run the research tools",
        detail:
          "Ask an Expert, Methodology Forum, Reverse Seminars and Project Builder.",
        to: "/tools",
      },
      {
        title: "Explore the data & climate stories",
        detail: "Compare any country in the Observatory and Visualising Wine.",
        to: "/observatory",
      },
      {
        title: "Contribute to & read the survey",
        detail: "Benchmark findings live on the dashboard and in the results.",
        to: "/results",
      },
      {
        title: "Turn a paper into a podcast",
        detail: "Upload a PDF and get a short audio summary.",
        to: "/tool/paper-to-podcast",
      },
      {
        title: "Complete your profile",
        detail: "Link ORCID / Scholar so other researchers can find you.",
        to: "/profile",
      },
      {
        title: "Join the theme discussions",
        detail: "Ask questions and compare notes with the community.",
        to: "/discussions",
      },
    ],
  },
  {
    key: "industry",
    label: "For wine producers, equipment makers & professionals",
    icon: FaWineBottle,
    blurb:
      "Get on the map, follow your markets, and meet partners and researchers.",
    items: [
      {
        title: "Add or claim your winery",
        detail: "Put your business on the global Observatory map.",
        to: "/winery/new",
      },
      {
        title: "Follow the themes that matter",
        detail:
          "Market analysis, marketing, sustainability, innovation, supply chain.",
        to: "/themes",
      },
      {
        title: "Find people & partners",
        detail: "Connect with researchers, partners and other professionals.",
        to: "/connections",
      },
      {
        title: "Attend events & seminars",
        detail: "Browse upcoming events and workshops across the network.",
        to: "/events",
      },
      {
        title: "Showcase your organisation",
        detail: "Add your company, role and links to your profile.",
        to: "/profile",
      },
    ],
  },
  {
    key: "policy",
    label: "For policymakers & institutions",
    icon: FaLandmark,
    blurb:
      "An evidence base built for policy — descriptive, sourced and citable.",
    items: [
      {
        title: "Explore Visualising Wine",
        detail: "Sourced, citable evidence on the global wine sector.",
        to: "/visualising-wine",
      },
      {
        title: "Read the country scorecards",
        detail: "Compare nations side by side on key indicators.",
        to: "/scorecard",
      },
      {
        title: "Follow the data & climate stories",
        detail: "How warming, variety and lock-in are reshaping wine.",
        to: "/story/warming",
      },
      {
        title: "Track the survey results",
        detail: "Headline findings from the global community survey.",
        to: "/results",
      },
      {
        title: "Connect with the institute",
        detail: "Reach regional committees and the people behind GWBI.",
        to: "/gwbi/people",
      },
    ],
  },
];

export const PAGE_GUIDES = {
  dashboard: {
    title: "Your dashboard",
    intro:
      "This is your home base — a live feed of research, news, events and conversations from across the global wine community. Post updates, follow the themes and regions you care about, and jump into the tools.",
    sections: [
      {
        heading: "Where to look",
        items: [
          "Left: your profile, your stats and a profile-completeness checklist — keep it up to date so others can find you.",
          "Middle: the feed. Write a post, filter it by type, and react to what others share.",
          "Right: upcoming events, member notices, surveys, and people to connect with.",
        ],
      },
    ],
    audiencesHeading: "What's here for you",
    audiences: DASHBOARD_AUDIENCES,
  },

  myWorld: {
    title: "My World",
    intro:
      "Your personal corner of the network — your connections, your events and your recent activity, gathered in one place.",
    sections: [
      {
        heading: "Where to look",
        items: [
          "Use the search box to jump to people, events or activity.",
          "Review who you're connected with and pick up recent conversations.",
          "See the events and updates relevant to you, without the full feed.",
        ],
      },
    ],
  },

  tools: {
    title: "Co-creation tools",
    intro:
      "The institute's seven core tools for working together — running research, asking experts, building projects and turning papers into podcasts. Each tool lives in a Theme, a Region, or both.",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Filter by Theme, Region or Both to find where a tool applies.",
          "Open any available tool to start; greyed-out tools are coming soon.",
          "Most tools are collaborative — invite others and build together.",
        ],
      },
    ],
  },

  discussions: {
    title: "Discussions",
    intro:
      "A shared space for members to ask questions, compare notes and keep the conversation going. Each theme has its own board, and boards can branch into focused sub-chats.",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Pick a theme to open its board, or start a new thread with a question.",
          "Reply to keep a thread alive; branch a sub-chat for a side topic.",
          "Attach files or links to share sources with the group.",
        ],
      },
    ],
  },

  observatory: {
    title: "Global Wine Observatory",
    intro:
      "Every winery in the world, mapped by its growing-season climate and practice — and how that climate has shifted since the 1980s. An evidence base you can explore, filter and cite.",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Search for a winery or filter by country in the top bar.",
          "Use 'Colour by' to recolour the map by climate or by practice.",
          "Add your own winery with the + Add button so it appears on the map.",
        ],
      },
    ],
  },

  profile: {
    title: "Your profile",
    intro:
      "How the rest of the network sees you. A complete profile — photo, headline, organisation, regions, themes and academic links — helps the right people find and connect with you.",
    sections: [
      {
        heading: "Where to look",
        items: [
          "Fill in your photo, headline and a short bio at the top.",
          "Pick the regions and themes you work on so you appear in the right places.",
          "Add academic links (ORCID, Scholar, ResearchGate) if you publish.",
          "Save when you're done — changes show across the site.",
        ],
      },
    ],
  },

  connections: {
    title: "Connections",
    intro:
      "Your professional network on the platform. Accept invitations, see who you're connected with, and discover researchers, producers and partners to reach out to.",
    sections: [
      {
        heading: "Where to look",
        items: [
          "Pending invitations appear at the top — accept or decline them there.",
          "Your existing connections are listed below; open anyone to view their profile.",
          "Use search and suggestions to find new people to connect with.",
        ],
      },
    ],
  },

  themes: {
    title: "Thematic Areas",
    intro:
      "The research themes that shape the institute's agenda — from market analysis and sustainability to governance and supply chains. Each theme is a hub of its own.",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Browse the themes and open one to enter its hub — leaders, tools, discussions and activity.",
          "The themes you follow are highlighted across the site's menus.",
          "Pick the themes that match your work so the right content finds you.",
        ],
      },
    ],
  },

  regions: {
    title: "Regional Clusters",
    intro:
      "Applied networks for local policy, market and community initiatives — the institute organised by part of the world. Each region is a hub of its own.",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Open a region to see its leaders, tools, discussions and local activity.",
          "The regions you follow are highlighted across the site's menus.",
          "Use regions to connect with people and projects near you.",
        ],
      },
    ],
  },

  notifications: {
    title: "Notifications",
    intro:
      "Everything that has happened across your network — connection requests, replies, mentions, events and updates — in one running list.",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Unread items are highlighted; click any one to jump straight to its source.",
          "Use 'Mark all read' to clear the list once you're caught up.",
          "The bell in the top bar shows your unread count from anywhere on the site.",
        ],
      },
    ],
  },

  podcasts: {
    title: "Podcasts",
    intro:
      "Research-driven audio from across the wine community — short, listenable summaries of papers, talks and conversations.",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Browse the episodes and play any one inline.",
          "Many episodes are generated from papers with the Paper-to-Podcast tool.",
          "Turn your own PDF into an episode from Tools › Paper-to-Podcast.",
        ],
      },
    ],
  },

  inbox: {
    title: "Leader Inbox",
    intro:
      "If you lead a theme or region, this is where requests and messages addressed to your groups arrive — kept separate from your personal notifications.",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Switch between the scopes you lead to see each group's messages.",
          "Open a message to read it in full and respond.",
          "Use Refresh to pull in anything that just arrived.",
        ],
      },
    ],
  },

  feed: {
    title: "Activity Feed",
    intro:
      "Events, challenges, documents and publications flowing across every theme and region — the platform's wider activity, beyond the people you follow.",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Filter by type to focus on events, documents, publications or challenges.",
          "Open any item to see its detail or source.",
          "This is the whole network's activity — your dashboard shows your own circle.",
        ],
      },
    ],
  },

  settings: {
    title: "Settings",
    intro:
      "Manage your account, privacy and preferences — who can see your profile, how you're contacted, and the language you use across the site.",
    sections: [
      {
        heading: "Where to look",
        items: [
          "Set your profile visibility and who is allowed to reach you.",
          "Choose your language and notification preferences.",
          "Update your account and security details.",
        ],
      },
    ],
  },

  members: {
    title: "Founding members",
    intro:
      "The institutions, universities and companies that stand behind the institute — its founding academic, industry and strategic partners.",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Browse the members in this category and open any one for details.",
          "Use search to find a particular institution or company.",
          "Switch categories from the GWBI menu to see academic, industry and strategic partners.",
        ],
      },
    ],
  },

  gwbiPeople: {
    title: "GWBI people",
    intro:
      "The people of the network — researchers, professionals and partners. Browse profiles and filter by area, country and topics of expertise.",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Filter by region, country or expertise to narrow the list.",
          "Open a profile to learn more about someone's work.",
          "Send a connection request to add them to your network.",
        ],
      },
    ],
  },

  events: {
    title: "Events",
    intro:
      "Conferences, seminars and workshops from across the global wine community — past and upcoming, in one calendar.",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Browse events by month and open any one for details and to RSVP.",
          "Create and host your own with the Event Creator.",
          "Invitations you receive also appear here.",
        ],
      },
    ],
  },

  cardview: {
    title: "Member grid",
    intro:
      "A visual grid of members across the network — a quick way to scan who's here and find people to connect with.",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Search and filter to narrow the grid to the people you want.",
          "Open any card to view that person's full profile.",
          "Connect with people directly from their card.",
        ],
      },
    ],
  },

  administration: {
    title: "GWBI Admin",
    intro:
      "The control room for the platform — manage members, content, surveys, governance and settings. Visible to administrators only.",
    sections: [
      {
        heading: "Where to look",
        items: [
          "Use the tabs across the top to move between areas (members, content, surveys, governance, system).",
          "The KPI cards summarise activity at a glance.",
          "Moderation and notification tools live under their respective tabs.",
        ],
      },
    ],
  },

  visualisingWine: {
    title: "Visualising Wine",
    intro:
      "A global, firm-level look at how the world makes wine — and how a warming climate is reshaping it, vineyard by vineyard. Built as an evidence base for policymakers and academics.",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Scroll through the data stories — each chart is sourced and citable.",
          "Compare countries and explore the climate shifts since the 1980s.",
          "Open the Observatory for the live, interactive winery map.",
        ],
      },
    ],
  },

  wineMarkets: {
    title: "Wine Markets",
    intro:
      "Two centuries of wine production, consumption and trade for forty countries, from the Anderson and Pinilla Annual Database of Global Wine Markets (University of Adelaide, CC BY 4.0).",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Pick an indicator in the left rail: production, consumption, trade.",
          "Add up to eight countries; tick World for the global line.",
          "Narrow the years with the range buttons and hover the chart to read values.",
          "Download the view as CSV. The source citation is at the foot of the page.",
        ],
      },
    ],
  },

  scorecard: {
    title: "Country portrait",
    intro:
      "A one-page, citable scorecard for any wine-producing country — its standout indicators, peer ranking and a plain-language read of the data.",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Pick a country from the selector to load its portrait.",
          "Choose a second country to compare the two side by side.",
          "Use Print to export a clean copy for reports or citation.",
        ],
      },
    ],
  },

  governance: {
    title: "Governance",
    intro:
      "The people who guide the institute — its boards, regional coordinators and theme leaders. See who's involved, or put your name forward for an open seat.",
    sections: [
      {
        heading: "How to use it",
        items: [
          "Open a group to see who holds each role.",
          "Register your interest in a vacant seat with the form provided.",
          "Roles map to the same themes and regions used across the site.",
        ],
      },
    ],
  },

  toolTheme: {
    title: "Theme hub",
    intro:
      "Everything for this research theme in one place — the leaders who steward it, a suite of co-creation tools, and the latest discussions and activity. Follow the theme to pin it and get its updates on your dashboard.",
    sections: [
      {
        heading: "Where to look",
        items: [
          "Meet the theme's leaders at the top — reach them through Ask the Expert.",
          "Use the tools below to collaborate; scroll down for discussions and recent activity.",
          "Hit Follow Theme to keep it on your dashboard and in your top menu.",
        ],
      },
      {
        heading: "The tools on this page",
        items: [
          {
            title: "Collaborative Project Builder",
            detail:
              "Launch a joint study: define roles and milestones, then open a call for collaborators.",
            to: "/tool/project/new",
          },
          {
            title: "Data Access & Methodology Forum",
            detail:
              "Share protocols, request datasets and compare methods in structured, searchable threads.",
            to: "/tool/methodology-forum",
          },
          {
            title: "Paper to Podcast Generator",
            detail:
              "Upload a paper and turn it into a short audio episode or practitioner-ready brief.",
            to: "/tool/paper-to-podcast",
          },
          {
            title: "Reverse Seminars (Challenge Board)",
            detail:
              "A seminar run in reverse: instead of an expert presenting findings, someone posts a real-world challenge — and the network responds with evidence and pilot proposals, moving from challenge brief to research response to pilot to decision. As an academic, browse challenges in your field to find applied problems to work on, or post your own to attract collaborators.",
            to: "/tool/reverse-seminars/new",
          },
          {
            title: "Event / Working Group Creator",
            detail:
              "Spin up a seminar, webinar or working group and coordinate the follow-up.",
            to: "/tool/event-creator",
          },
          {
            title: "Ask the Expert (Quick Q&A)",
            detail:
              "Send a focused question to this theme's leaders and get a moderated expert reply.",
            to: "/tool/ask-expert/new",
          },
          {
            title: "Data Visualization Builder",
            detail:
              "Build charts and visual stories from shared GWBI datasets or your own uploads.",
            to: "/tool/data-viz",
          },
        ],
      },
    ],
  },

  toolRegion: {
    title: "Region hub",
    intro:
      "Everything for this regional cluster in one place — the coordinators who lead it, applied co-creation tools, and local discussions and activity. Follow the region to pin it and get its updates on your dashboard.",
    sections: [
      {
        heading: "Where to look",
        items: [
          "Meet the region's coordinators at the top — reach them through Ask the Expert.",
          "Use the tools below for applied, local work; scroll down for discussions and activity.",
          "Hit Follow Region to keep it on your dashboard and in your top menu.",
        ],
      },
      {
        heading: "The tools on this page",
        items: [
          {
            title: "Local Challenge Board",
            detail:
              "A 'reverse seminar' for local problems: operators and policymakers post a real challenge — the problem, the outcome they want and a timeline — and researchers reply with evidence-backed pilots. Find an applied problem to take on, or post one to invite responses.",
            to: "/tool/reverse-seminars/new",
          },
          {
            title: "Regional Event Builder",
            detail:
              "Organise regional meetups, webinars and working groups, and coordinate the local follow-up.",
            to: "/tool/event-creator",
          },
          {
            title: "Ask the Expert (Quick Q&A)",
            detail:
              "Send a focused question to this region's coordinators for policy, market or technical input.",
            to: "/tool/ask-expert/new",
          },
          {
            title: "Data Visualization Builder",
            detail:
              "Build regional charts and visual stories from shared datasets and your own uploads.",
            to: "/tool/data-viz",
          },
        ],
      },
    ],
  },
};

export default PAGE_GUIDES;
